import type { SolutionVersionConfig } from '@pypestream/pro-studio-collab-client';

/**
 * The Supervisor panel under live editing (PSTUDIO-4451). Its config and
 * branding fields live in the collab document's `config` and save with one
 * `updateConfig` (which mirrors brand, supervisor name and tone into the
 * supervisor node itself); its tasks are the document's task nodes — the
 * rows carrying `settings.taskId` — with the name in `nodeName` and the
 * description in `utterances`, as `createTaskNode` lays them out.
 */
type TaskRow = {
  id: string;
  nodeName?: string | null;
  utterances?: string | null;
  settings?: { taskId?: string } | null;
};

// As the panel's react-hook-form values type them: every field may be unset.
export type SupervisorConfigValues = {
  supervisorName?: string;
  otherInformation?: string;
  temperature?: number;
  maxOutputTokens?: number;
  topP?: number;
  topK?: number;
  llmCredentialId?: string;
};
export type SupervisorAgentValues = { brandName?: string; tone?: string };
export type SupervisorTaskValue = {
  name: string;
  description?: string;
  taskId?: string;
};

const isTaskRow = (row: TaskRow) => !!row.settings?.taskId;

/** The document's task nodes, in the row shape the helpers above take. */
export const taskRowsFromNodes = (
  nodes: Iterable<{
    id: string;
    isTask: boolean;
    nodeName: string | null;
    utterances: string | null;
  }>
): TaskRow[] =>
  [...nodes]
    .filter((node) => node.isTask)
    .map((node) => ({
      id: node.id,
      nodeName: node.nodeName,
      utterances: node.utterances,
      settings: { taskId: node.id },
    }));

/** The three forms' values from the document — with the legacy fallbacks. */
export const supervisorDefaultsFromCollab = (
  config: SolutionVersionConfig,
  rows: readonly TaskRow[]
): {
  config: SupervisorConfigValues;
  agent: SupervisorAgentValues;
  tasks: { tasks: SupervisorTaskValue[] };
} => ({
  config: {
    supervisorName: config.supervisorName || '',
    otherInformation: config.otherInformation || '',
    temperature: config.temperature || 0.2,
    maxOutputTokens: config.maxOutputTokens || 2048,
    topP: config.topP || 0.8,
    topK: config.topK || 0.2,
    llmCredentialId: config.llmCredentialId ?? undefined,
  },
  agent: { brandName: config.brandName || '', tone: config.tone || '' },
  tasks: {
    tasks: rows.filter(isTaskRow).map((row) => ({
      name: row.nodeName || '',
      description: row.utterances || '',
      taskId: row.id,
    })),
  },
});

const orNull = <T>(value: T | '' | undefined): T | null =>
  value === '' || value === undefined ? null : value;

/** One `updateConfig` patch for the config and branding forms. */
export const toConfigPatch = (
  config: SupervisorConfigValues,
  agent: SupervisorAgentValues
): Partial<SolutionVersionConfig> => ({
  supervisorName: orNull(config.supervisorName),
  otherInformation: orNull(config.otherInformation),
  temperature: config.temperature ?? null,
  maxOutputTokens: config.maxOutputTokens ?? null,
  topP: config.topP ?? null,
  topK: config.topK ?? null,
  llmCredentialId: orNull(config.llmCredentialId),
  brandName: orNull(agent.brandName),
  tone: orNull(agent.tone),
});

/**
 * What the tasks form means for the task nodes: create, change, remove.
 * `rows` is the DOCUMENT's task nodes — not the table's rows, which the tasks
 * tab edits locally as the user types and would hide every rename. Only tasks
 * the form held when it opened (`heldIds`) can be removed: a task a coworker
 * added meanwhile is not in the form, and this save must not delete it.
 */
export const planTaskSave = (
  formTasks: readonly SupervisorTaskValue[],
  { rows, heldIds }: { rows: readonly TaskRow[]; heldIds: readonly string[] },
  newId: () => string
): {
  create: Array<{ id: string; name: string; description: string }>;
  update: Array<{ rowId: string; name: string; description: string }>;
  remove: string[];
} => {
  const taskRows = new Map(rows.filter(isTaskRow).map((row) => [row.id, row]));
  const kept = new Set<string>();
  const create: Array<{ id: string; name: string; description: string }> = [];
  const update: Array<{ rowId: string; name: string; description: string }> =
    [];

  for (const task of formTasks) {
    const description = task.description ?? '';
    const row = task.taskId ? taskRows.get(task.taskId) : undefined;
    if (!row) {
      create.push({
        id: newId(),
        name: task.name,
        description,
      });
      continue;
    }
    kept.add(row.id);
    if (
      (row.nodeName || '') !== task.name ||
      (row.utterances || '') !== description
    ) {
      update.push({
        rowId: row.id,
        name: task.name,
        description,
      });
    }
  }

  const remove = heldIds.filter((id) => taskRows.has(id) && !kept.has(id));
  return { create, update, remove };
};
